import type { Params } from '@angular/router';
import type { Action } from '@ngrx/store';
import type { SiteCreateRequest, SiteEntity, SiteListEntity, ShareBlocks } from '@common/types/site.type';
import { ContentSourceType } from '@common/types/site.type';
import { type ShopCartIconSettingsType } from '@common/types/shop-cart-icon-settings.type';
import { type SitePageEntity, type SitePageUpdateRequest } from '@common/types/site-page.type';
import type { Template, TemplateUpdateRequest } from '@common/types/template.type';
import type { ThemeSettingsState } from '@sites/site-theme/src/state';
import { formatFontsLinkAsImport } from '@sites/site-theme/src/helpers';
import type { DenormalizedBlockEntity } from '@web/types';
import type { Domain } from '@common/types/domain.type';
import type { Id, Uuid } from '@common/types';

import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { EContentSources, EPageStatuses, ESaveErrors, ESiteStatuses } from '@common/enums';
import { getUuid, isUuid, stringifyPageStructure } from '@common/helpers';
import { EDrawerType } from '@common/types/layout.type';

import { PageStructureService } from '@web/store/page-structure/page-structure/page-structure.service';

import NotifyAction from '@common/utils/notify/notify.actions';
import PagesActions from '@web/store/website/pages/pages.actions';
import ContentActions from '@web/store/website/content/content.actions';
import AppActions from '@web/store/app/app.actions';
import HistoryActions from '@web/store/history/history.actions';
import LayoutActions from '@web/store/layout/layout.actions';
import SiteActions from './site.actions';
import { serializeThemeSettings } from '@sites/site-theme';
import { blockIsShared } from '@sites/entity-core';
import type { StoredTheme } from './stored-theme';

type PreparePage = {
    structure: DenormalizedBlockEntity[];
    themeSettings: ThemeSettingsState;
    /** Written instead of `themeSettings` when set — see `themeToPreserve`. */
    storedTheme?: StoredTheme | null;
    sharedBlocks: ShareBlocks;
    contentSource: ContentSourceType;
};

type PreparePreview = {
    structure: DenormalizedBlockEntity[];
    themeSettings: ThemeSettingsState;
    showPoweredBy: boolean;
    isEnabledAnimations: boolean;
    siteId: string;
    pageId: string;
    sharedBlocks: ShareBlocks;
    cartIconData: ShopCartIconSettingsType;
    isCartEnabled: boolean;
    domain: Domain;
    contentSourceId: EContentSources;
    userId?: number;
    userCreatedAt?: string | null;
};

@Injectable({
    providedIn: 'root',
})
export class SiteService {
    private readonly translateService = inject(TranslateService);
    private readonly structureService = inject(PageStructureService);

    private readonly CONTENT_SOURCE_MAP = {
        [EContentSources.Website]: ContentSourceType.Website,
        [EContentSources.MiniLanding]: ContentSourceType.MiniLanding,
        [EContentSources.Education]: ContentSourceType.Education,
        [EContentSources.WebEducation]: ContentSourceType.WebEducation,
        [EContentSources.ChatBot]: ContentSourceType.ChatBot,
        [EContentSources.PopUp]: ContentSourceType.PopUp,
        [EContentSources.WebChatBot]: ContentSourceType.WebChatBot,
    };

    public setInitialTemplateData(template: Template): void {
        // const structure = JSON.parse(template.contents.structure) || [];
        // const themeSettings = JSON.parse(template.contents.theme_settings);
        //
        // this.setInitialData(structure, themeSettings);
    }

    public preparePageData({
        structure,
        themeSettings,
        storedTheme = null,
        sharedBlocks,
        contentSource,
        ...rest
    }:
        | PreparePage
        | (PreparePage & {
              name: string;
              url: string;
              // status is mandatory when name and url are provided
              status: EPageStatuses;
              publish?: boolean;
          })) {
        const pagePayload: {
            content_source: ContentSourceType;
            content: { name: string; value: string }[];
            name?: string;
            url?: string;
        } = {
            content_source: contentSource,
            content: [
                {
                    name: 'draft_structure',
                    value: stringifyPageStructure(structure),
                },
                {
                    name: 'draft_theme_settings',
                    // Stamped with the entity version rather than serialized raw, so the next read
                    // takes the versioned path instead of the legacy one — the migration's job
                    // shrinks with every save instead of being paid forever.
                    value: storedTheme ? storedTheme.settings : serializeThemeSettings(themeSettings),
                },
                {
                    name: 'draft_theme_settings_styles',
                    value: storedTheme?.styles ?? formatFontsLinkAsImport(themeSettings.fonts),
                },
                {
                    name: 'draft_shared_blocks',
                    value: stringifyPageStructure(sharedBlocks),
                },
            ],
        };

        if ('status' in rest && 'publish' in rest) {
            const { name, url, status, publish } = rest;

            if (status !== EPageStatuses.New || !publish) {
                return pagePayload;
            }

            if (name && url) {
                // when creating draft without modal we dont have url, so backend will trhow 422 if we send name without url
                pagePayload.name = name;
            }
            if (url) {
                pagePayload.url = url;
            }
        }

        return pagePayload;
    }

    public formatSaveData({
        structure,
        themeSettings,
        storedTheme = null,
        sharedBlocks = null,
        templateId = null,
        publish,
    }: {
        structure: DenormalizedBlockEntity[];
        themeSettings: ThemeSettingsState;
        storedTheme?: StoredTheme | null;
        sharedBlocks: ShareBlocks;
        templateId: string | null;
        publish?: boolean;
    }): SiteCreateRequest {
        const payload: any = this.preparePageData({
            structure,
            themeSettings,
            storedTheme,
            sharedBlocks,
            contentSource: ContentSourceType.Website,
            publish,
        });

        payload.templateId = Number(templateId);
        return payload;
    }

    public formatCreatePageData({
        structure,
        sharedBlocks,
        themeSettings,
        storedTheme = null,
        page,
        templateId = null,
        publish = false,
        contentSource = ContentSourceType.Website,
    }: {
        structure: DenormalizedBlockEntity[];
        themeSettings: ThemeSettingsState;
        storedTheme?: StoredTheme | null;
        sharedBlocks: ShareBlocks;
        page: SitePageEntity;
        templateId: string | null;
        publish: boolean;
        contentSource: ContentSourceType;
    }): SitePageUpdateRequest {
        const pagePayload: SitePageUpdateRequest & { templateId?: number } = this.preparePageData({
            structure,
            themeSettings,
            storedTheme,
            sharedBlocks,
            contentSource,
            name: page.name,
            url: page.url,
            status: page.status,
            publish,
        });

        pagePayload.templateId = Number(templateId);
        return pagePayload;
    }

    public formatUpdateData(
        newSiteData: Partial<SiteCreateRequest> | null,
        page: SitePageEntity,
        structure: DenormalizedBlockEntity[],
        themeSettings: ThemeSettingsState,
        sharedBlocks: ShareBlocks,
        publish: boolean,
        version?: string,
        storedTheme: StoredTheme | null = null
    ): SitePageUpdateRequest {
        return {
            ...this.preparePageData({
                structure,
                themeSettings,
                storedTheme,
                sharedBlocks,
                contentSource: this.CONTENT_SOURCE_MAP[page?.content_source_id] ?? ContentSourceType.Website,
                name: newSiteData?.name ?? page.name,
                url: page?.url ? new URL(page.url).pathname.replace('/', '') : undefined,
                status: page.status,
                publish,
            }),
            version,
        };
    }

    public formatUpdateAdminTemplate(
        template: Template,
        structure: any[],
        themeSettings: ThemeSettingsState,
        storedTheme: StoredTheme | null = null
    ): TemplateUpdateRequest {
        const payload = {
            name: template.name,
            lang: template.lang,
            category_ids: template.categories.map((c) => c.id),
            description: template.description,
            tags: template.tags,
            content_source: ContentSourceType.Website,
            content: [
                {
                    name: 'structure',
                    value: stringifyPageStructure(structure),
                },
                {
                    name: 'theme_settings',
                    value: storedTheme ? storedTheme.settings : serializeThemeSettings(themeSettings),
                },
                {
                    name: 'theme_settings_styles',
                    value: storedTheme?.styles ?? formatFontsLinkAsImport(themeSettings.fonts),
                },
            ],
        };

        const categoryIds = template.categories.map((c) => c.id);

        if (categoryIds.length) {
            payload.category_ids = categoryIds;
        }

        return payload;
    }

    public formatPreviewData({
        structure,
        sharedBlocks,
        themeSettings,
        showPoweredBy,
        siteId,
        pageId,
        cartIconData,
        isCartEnabled,
        domain,
        contentSourceId,
        isEnabledAnimations,
        userId,
        userCreatedAt,
    }: PreparePreview) {
        // `blockIsShared`, not `meta`: a block the canvas could not migrate is carried here raw.
        const preparedStructure = structure.filter((block) => !blockIsShared(block));
        const preparedSharedBlocks = this.structureService.mapBlockEntitiesToSharedBlocks(sharedBlocks, structure);
        return {
            structure: JSON.stringify(preparedStructure),
            themeSettings: serializeThemeSettings(themeSettings),
            showPoweredBy,
            isEnabledAnimations,
            page: {
                id: isUuid(pageId) ? pageId : Number(pageId),
            },
            sharedBlocks: JSON.stringify(preparedSharedBlocks),
            domain: domain ? { id: domain.id, url: domain.url } : null,
            contentSourceId,
            site: {
                id: isUuid(siteId) ? siteId : Number(siteId),
                settings: {
                    shop: {
                        cart_icon: {
                            icon_bg_color: cartIconData?.iconBgColor,
                            icon_color: cartIconData?.iconColor,
                            icon_counter_bg_color: cartIconData?.iconCounterBgColor,
                            icon_counter_color: cartIconData?.iconCounterColor,
                            icon_size: cartIconData?.iconSize,
                            icon_type: cartIconData?.iconType,
                            icon_rounding: cartIconData?.iconRounding,
                            icon_position_type: cartIconData?.iconPositionType,
                        },
                        enabled: isCartEnabled,
                    },
                },
            },
            userId: userId,
            user: { createdAt: userCreatedAt ?? null },
        };
    }

    public createEmptySite(): Partial<SiteEntity> {
        return {
            id: getUuid(),
            name: this.translateService.instant('control_new_site'),
            status: ESiteStatuses.New,
        };
    }

    public isNewSite({ siteId, pageId }: Params, isAdmin: boolean = false): boolean {
        return (!isAdmin && !siteId && !pageId) || (isUuid(siteId) && isUuid(pageId));
    }

    public isNewPage({ siteId, pageId }: Params, isAdmin: boolean = false): boolean {
        return (!isAdmin && siteId && !isUuid(siteId) && !pageId) || isUuid(pageId);
    }

    public isNewSiteFromTemplate({ siteId, pageId }: Params, { templateId }: Params): boolean {
        return !!templateId && (!siteId || isUuid(siteId)) && (!pageId || isUuid(pageId));
    }

    public isNewPageFromTemplate({ siteId, pageId }: Params, { templateId }: Params): boolean {
        return !!templateId && siteId && !isUuid(siteId) && (!pageId || isUuid(pageId));
    }

    public isEcommerceMode({ siteId }: Params, routerUrl: string): boolean {
        return !!siteId && routerUrl.includes('/ecommerce');
    }

    public isSitePublished(site: SiteEntity | SiteListEntity): boolean {
        return Boolean(site?.pub_date);
    }

    public isSiteActive(site: SiteEntity | SiteListEntity): boolean {
        return site?.status === ESiteStatuses.Active;
    }

    private saveNotyShowed: boolean = false;

    public handleSaveSiteSuccess(data: {
        site: SiteEntity;
        page: SitePageEntity;
        publish?: boolean;
        exit?: boolean;
        navigateTo?: { pageId?: string; siteId?: string; isNew?: boolean };
        skipContentChange?: boolean;
        isAdmin: boolean;
        resetContent?: boolean;
        isRedirectTabClose?: boolean;
    }): Action[] {
        const {
            site,
            page,
            navigateTo,
            publish,
            exit,
            isAdmin = false,
            skipContentChange = true,
            resetContent = false,
            isRedirectTabClose = false,
        } = data;
        const actions: Action[] = [
            SiteActions.saveSiteSuccess({ site }),
            ContentActions.setIsDirty({ isDirty: false }),
        ];

        if (!this.saveNotyShowed) {
            this.saveNotyShowed = true;
            actions.push(
                NotifyAction.showNotify({
                    toastType: 'success',
                    message: this.translateService.instant('all_changes_auto_saved'),
                })
            );
        }

        if (!isAdmin) {
            actions.push(
                PagesActions.navigateSitePage({
                    siteId: navigateTo?.siteId || site.id.toString(),
                    pageId: navigateTo?.pageId || page.id.toString(),
                    skipContentChange,
                })
            );

            if (publish) {
                actions.push(
                    PagesActions.activatePageRequest({
                        siteId: site.id.toString(),
                        pageId: page.id.toString(),
                        resetContent,
                    })
                );
            }

            if (exit) {
                actions.push(AppActions.exit({ isRedirectTabClose }));
            }
        }

        if (!navigateTo) {
            return actions;
        }

        if (navigateTo?.isNew) {
            actions.push(PagesActions.createNewPage());
            return actions;
        }

        return actions;
    }

    /**
     * The open page was deleted somewhere else — found by a save answered with 404 or by the version
     * poll. It leaves the page list, and saves to it are refused from now on: they would only be lost.
     */
    public pageDeletedActions(pageId: Id | Uuid): Action[] {
        return [
            ContentActions.markPageDeleted({ pageId }),
            PagesActions.deletePageSuccess({ pageId }),
            NotifyAction.showNotify({
                toastType: 'warning',
                message: this.translateService.instant('page_deleted_elsewhere'),
            }),
            LayoutActions.openDrawer({ drawerState: { drawerType: EDrawerType.page } }),
        ];
    }

    public handleSaveSiteError(
        error: any,
        status?: number,
        reloadContext?: { siteId: Id | Uuid; pageId: Id | Uuid }
    ): Action[] {
        if (status === 409 && reloadContext) {
            return [
                NotifyAction.showNotify({
                    toastType: 'error',
                    message: this.translateService.instant('page_save_conflict'),
                }),
                ContentActions.markPageVersionStale({ pageId: reloadContext.pageId }),
                LayoutActions.hideSelectedEntitySettings(),
                HistoryActions.resetHistory(),
                ContentActions.loadPageContentRequest({
                    siteId: reloadContext.siteId,
                    pageId: reloadContext.pageId,
                    reloadSite: true,
                }),
                SiteActions.saveSiteFailure({ error: null }),
                ContentActions.setIsDirty({ isDirty: false }),
            ];
        }

        if (status === 404 && reloadContext) {
            return [
                ...this.pageDeletedActions(reloadContext.pageId),
                SiteActions.saveSiteFailure({ error: null }),
                ContentActions.setIsDirty({ isDirty: false }),
            ];
        }

        if (status === 0) {
            return [SiteActions.saveSiteFailure({ error: null }), SiteActions.saveSiteOffline()];
        }

        if ('errors' in error) {
            return [
                SiteActions.saveSiteFailure({ error: error.errors }),
                ContentActions.setIsDirty({ isDirty: false }),
            ];
        }

        if (error && (!error?.error_code || error?.error_code === ESaveErrors.UNKNOWN_ERROR)) {
            return [
                NotifyAction.showNotify({
                    toastType: 'error',
                    message: this.translateService.instant('unknown_error'),
                }),
                SiteActions.saveSiteFailure({ error: null }),
                ContentActions.setIsDirty({ isDirty: false }),
            ];
        }

        return [
            SiteActions.saveSiteFailure({ error: error?.error_code }),
            ContentActions.setIsDirty({ isDirty: false }),
        ];
    }

    public prepareTemplate(template: Template): Template {
        return {
            ...template,
        };
    }
}
